/**
 * Crash-proof mapper from unknown errors → user-facing English strings (DEV-290).
 *
 * Never throws. Always returns a non-empty string. Never surfaces SDK/raw chrome.
 *
 * @module shared/errors/toUserFacingMessage
 */

import {
  ACTION_DEFAULTS,
  CODE_TO_MESSAGE_KEY,
  DEFAULT_ERROR_MESSAGE,
  ERROR_MESSAGES,
  MAX_SERVER_MESSAGE_LENGTH,
  type ActionDefaultKey,
} from './messages';

export interface ToUserFacingMessageOptions {
  /** Action-scoped default when code does not map. */
  action?: ActionDefaultKey;
  /** Optional override default; still never empty. */
  defaultMessage?: string;
}

interface ExtractedError {
  code: string | null;
  message: string | null;
  detailsReason: string | null;
  detailsCode: string | null;
  status: number | null;
}

const RAW_STATUS_WORDS = new Set([
  'unavailable',
  'internal',
  'unknown',
  'cancelled',
  'canceled',
  'deadline_exceeded',
  'resource_exhausted',
  'unauthenticated',
  'permission_denied',
  'not_found',
  'already_exists',
  'failed_precondition',
  'invalid_argument',
  'aborted',
  'out_of_range',
  'unimplemented',
  'data_loss',
]);

function safeString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Best-effort extract without throwing on weird shapes / circular refs.
 */
function safeExtract(error: unknown): ExtractedError {
  const empty: ExtractedError = {
    code: null,
    message: null,
    detailsReason: null,
    detailsCode: null,
    status: null,
  };

  if (error == null) return empty;

  if (typeof error === 'string') {
    return { ...empty, message: safeString(error) };
  }

  if (typeof error !== 'object') {
    try {
      return { ...empty, message: safeString(String(error)) };
    } catch {
      return empty;
    }
  }

  try {
    const e = error as Record<string, unknown>;
    const code = safeString(e.code);
    const message = safeString(e.message);
    let detailsReason: string | null = null;
    let detailsCode: string | null = null;
    const details = e.details;
    if (details && typeof details === 'object') {
      const d = details as Record<string, unknown>;
      detailsReason = safeString(d.reason);
      detailsCode = safeString(d.code);
    }
    const status = typeof e.status === 'number' && Number.isFinite(e.status) ? e.status : null;
    return { code, message, detailsReason, detailsCode, status };
  } catch {
    return empty;
  }
}

function normalizeCode(code: string | null): string | null {
  if (!code) return null;
  return code.trim().toLowerCase();
}

/**
 * True when a string looks like SDK / transport chrome that must never be shown.
 */
export function looksLikeSdkChrome(message: string): boolean {
  const m = message.trim();
  if (!m) return true;
  const lower = m.toLowerCase();

  if (/^firebase\b/i.test(m)) return true;
  if (/\bauth\//i.test(m)) return true;
  if (/\bfunctions\//i.test(m)) return true;
  if (/\[[^\]]+\/[^\]]+\]/.test(m)) return true; // [auth/network-request-failed]
  if (/httpserror/i.test(m)) return true;
  if (/firebaseerror/i.test(m)) return true;

  const compact = lower.replace(/[\s_-]+/g, '');
  if (RAW_STATUS_WORDS.has(lower) || RAW_STATUS_WORDS.has(compact)) return true;
  if (/^(grpc|status)[_:\s]/i.test(m)) return true;

  return false;
}

/**
 * Server messages allowed only for validation-style codes after sanitize.
 */
export function isAllowlistedServerMessage(message: string): boolean {
  if (typeof message !== 'string') return false;
  const trimmed = message.trim();
  if (!trimmed || trimmed.length > MAX_SERVER_MESSAGE_LENGTH) return false;
  if (looksLikeSdkChrome(trimmed)) return false;
  if (trimmed.toLowerCase().includes('internal')) return false;
  return true;
}

function resolveDefault(options?: ToUserFacingMessageOptions): string {
  const override = options?.defaultMessage;
  if (typeof override === 'string' && override.trim().length > 0) {
    return override.trim();
  }
  if (options?.action && ACTION_DEFAULTS[options.action]) {
    return ACTION_DEFAULTS[options.action];
  }
  return DEFAULT_ERROR_MESSAGE;
}

function lookupByCode(code: string | null): string | null {
  const normalized = normalizeCode(code);
  if (!normalized) return null;

  const key = CODE_TO_MESSAGE_KEY[normalized];
  if (key && ERROR_MESSAGES[key]) return ERROR_MESSAGES[key];

  // Strip functions/ and retry bare code
  if (normalized.startsWith('functions/')) {
    const bare = normalized.slice('functions/'.length);
    const bareKey = CODE_TO_MESSAGE_KEY[bare];
    if (bareKey && ERROR_MESSAGES[bareKey]) return ERROR_MESSAGES[bareKey];
  }

  return null;
}

function isValidationStyleCode(code: string | null): boolean {
  const n = normalizeCode(code);
  if (!n) return false;
  const bare = n.startsWith('functions/') ? n.slice('functions/'.length) : n;
  return (
    bare === 'invalid-argument' ||
    bare === 'failed-precondition' ||
    bare === 'not-found' ||
    bare === 'resource-exhausted'
  );
}

function isNetworkLike(extracted: ExtractedError): boolean {
  const code = normalizeCode(extracted.code);
  const msg = (extracted.message || '').toLowerCase();
  if (
    code === 'unavailable' ||
    code === 'functions/unavailable' ||
    code === 'auth/network-request-failed'
  ) {
    return true;
  }
  if (msg === 'unavailable' || msg.includes('network-request-failed')) {
    return true;
  }
  if (
    msg.includes('network error') ||
    msg.includes('unreachable host') ||
    msg.includes('failed to fetch')
  ) {
    return true;
  }
  return false;
}

function isDataConnectEmulator(extracted: ExtractedError): boolean {
  const code = normalizeCode(extracted.code);
  const detailsCode = normalizeCode(extracted.detailsCode);
  if (code !== 'functions/internal' && code !== 'internal') return false;
  if (detailsCode !== 'econnrefused') return false;
  // Port check would need details.port — best-effort via message
  return true;
}

function isDuplicateEmail(extracted: ExtractedError): boolean {
  const code = normalizeCode(extracted.code);
  if (
    code === 'auth/email-already-in-use' ||
    code === 'auth/email-already-exists' ||
    code === 'already-exists' ||
    code === 'functions/already-exists'
  ) {
    return true;
  }
  const msg = (extracted.message || '').toLowerCase();
  if (!msg) return false;
  return (
    (msg.includes('email') && msg.includes('already in use')) ||
    msg.includes('email address is already in use') ||
    msg.includes('email-already-exists')
  );
}

function isSignupSessionExpired(extracted: ExtractedError): boolean {
  const msg = extracted.message || '';
  return msg.includes('recently created') || msg.includes('only phone authentication');
}

/**
 * Convert any thrown value into a friendly, non-empty user-facing string.
 * Guaranteed not to throw.
 */
export function toUserFacingMessage(error: unknown, options?: ToUserFacingMessageOptions): string {
  try {
    const fallback = resolveDefault(options);
    const extracted = safeExtract(error);
    const codeNorm = normalizeCode(extracted.code);

    if (isDataConnectEmulator(extracted)) {
      return ERROR_MESSAGES['emulator.dataConnect'];
    }

    if (isNetworkLike(extracted)) {
      return ERROR_MESSAGES.network;
    }

    // Child create uses username, not email — don't show email-already-exists copy
    if (
      options?.action === 'createChild' &&
      (codeNorm === 'already-exists' || codeNorm === 'functions/already-exists')
    ) {
      if (extracted.message && isAllowlistedServerMessage(extracted.message)) {
        return extracted.message.trim();
      }
      return ERROR_MESSAGES['username.taken'];
    }

    // Parent → child switch: map before generic permission-denied / not-found allowlist
    // (consent is masked by functions.permissionDenied; not-found can leak child UUIDs).
    if (options?.action === 'switchChild') {
      if (codeNorm === 'permission-denied' || codeNorm === 'functions/permission-denied') {
        const msg = extracted.message || '';
        if (/consent/i.test(msg) || /revoked/i.test(msg)) {
          return ERROR_MESSAGES['child.consentRequired'];
        }
        return ERROR_MESSAGES['child.consentRequired'];
      }
      if (codeNorm === 'not-found' || codeNorm === 'functions/not-found') {
        return ERROR_MESSAGES['child.notFound'];
      }
      if (codeNorm === 'unauthenticated' || codeNorm === 'functions/unauthenticated') {
        return ERROR_MESSAGES['functions.unauthenticated'];
      }
    }

    if (isDuplicateEmail(extracted)) {
      return ERROR_MESSAGES['auth.emailAlreadyInUse'];
    }

    // Permission + signup session expiry special case
    if (
      (codeNorm === 'permission-denied' || codeNorm === 'functions/permission-denied') &&
      isSignupSessionExpired(extracted)
    ) {
      return ERROR_MESSAGES['signup.sessionExpired'];
    }

    // Catalog by code
    const fromCode = lookupByCode(extracted.code);
    if (fromCode) {
      // Prefer allowlisted server message for validation-style codes
      if (
        isValidationStyleCode(extracted.code) &&
        extracted.message &&
        isAllowlistedServerMessage(extracted.message)
      ) {
        return extracted.message.trim();
      }
      return fromCode;
    }

    // Explicit domain details.reason (purchasable / structured callables)
    if (extracted.detailsReason && isAllowlistedServerMessage(extracted.detailsReason)) {
      return extracted.detailsReason.trim();
    }

    // Validation-style without catalog hit — still allow sanitized server message
    if (
      isValidationStyleCode(extracted.code) &&
      extracted.message &&
      isAllowlistedServerMessage(extracted.message)
    ) {
      return extracted.message.trim();
    }

    // Unknown auth/* → generic auth copy (never raw)
    if (codeNorm?.startsWith('auth/')) {
      return ERROR_MESSAGES.authentication;
    }

    // Unknown functions/* or internal-looking → default
    if (
      codeNorm?.startsWith('functions/') ||
      codeNorm === 'internal' ||
      (extracted.status != null && extracted.status >= 500)
    ) {
      return fallback;
    }

    // Message that is literally UNAVAILABLE / INTERNAL etc.
    if (extracted.message && looksLikeSdkChrome(extracted.message)) {
      if (isNetworkLike(extracted) || /unavailable/i.test(extracted.message)) {
        return ERROR_MESSAGES.network;
      }
      return fallback;
    }

    return fallback;
  } catch {
    try {
      const fallback = resolveDefault(options);
      return fallback || DEFAULT_ERROR_MESSAGE;
    } catch {
      return DEFAULT_ERROR_MESSAGE;
    }
  }
}
